/**
 * Complete check: rotate the allowlisted demo login through every rotator
 * role in one page session (no reload), run the selected scenarios as each
 * JWT, then restore the starting role.
 *
 * District Admin is included on the dev-only allowlisted login.
 */

import { doc, getDoc, getDocFromServer } from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from '@/store/authStore'
import { getCurrentSchoolId } from '@/composables/useSchoolContext'
import { useSchoolStore } from '@/store/schoolStore'
import { normalizeUserGovernance } from '@/utils/userGovernance'
import { ROLE_DISPLAY_NAMES } from '@/config/roles'
import { WORKSPACE_ROTATOR_ROLES } from '@/utils/workspaceRoleRotator'
import { devLog, devError } from '@/utils/environment'
import { isSmokeRunnerAvailable } from './availability.js'
import { loadSmokeAuthInspection } from './authContext.js'
import { rotateDemoRoleInSession } from './roleRotate.js'
import { pendingSmokeProfile, restoreSmokeProfile } from './fixtureSession.js'
import { cleanupSmokeArtifacts } from './cleanup.js'
import { executeSmokeScenarios, scenarios } from './orchestrator.js'
import { scenarioStatusFromSteps, summarizeSmokeResults } from './stepStatus.js'
import {
  COMPLETE_CHECK_CAVEAT,
  assertCompleteCheckAllowed,
  completeCheckRoleValues,
  resolveCompleteCheckRoles,
  scenarioIdsForPreset as presetFromList
} from './completeCheckPlan.js'

export {
  COMPLETE_CHECK_CAVEAT,
  assertCompleteCheckAllowed,
  completeCheckRoleValues,
  resolveCompleteCheckRoles
}

export function scenarioIdsForPreset(includeWrites, allScenarios = scenarios) {
  return presetFromList(includeWrites, allScenarios)
}

function roleLabel(role) {
  return (
    ROLE_DISPLAY_NAMES[role] || WORKSPACE_ROTATOR_ROLES.find((r) => r.value === role)?.label || role
  )
}

function resolveActiveSchoolId() {
  try {
    const schoolStore = useSchoolStore()
    if (schoolStore.currentSchoolId) return schoolStore.currentSchoolId
  } catch {
    // Pinia not active
  }
  return null
}

function emitProgress(onProgress, results, extra = {}) {
  onProgress?.({ results, ...extra })
}

/**
 * Rotate through every rotator role, run the selected scenarios as each JWT,
 * optionally Admin-SDK-clean after write roles, then restore the starting role.
 *
 * @param {string[]} scenarioIds
 * @param {{ includeWrites?: boolean, keepArtifacts?: boolean, roles?: string[]|null, onProgress?: Function }} opts
 *   roles — rotator roles to run. Null/omitted runs every rotator role.
 */
export async function runCompleteSmokeCheck(
  scenarioIds,
  { includeWrites = false, keepArtifacts = false, roles = null, onProgress, onReport } = {}
) {
  if (!isSmokeRunnerAvailable()) {
    throw new Error('Smoke runner is not available in this build/project.')
  }

  const authStore = useAuthStore()
  const loaded = await loadSmokeAuthInspection(authStore.currentUser)
  if (!loaded.signedIn || !loaded.user) {
    throw new Error('No signed-in user — sign in before running the complete check.')
  }
  if (loaded.inspection.blocked) {
    throw new Error(loaded.inspection.reasons.join(' '))
  }

  const allowed = assertCompleteCheckAllowed({
    email: loaded.user.email,
    jwtRole: loaded.inspection.jwtRole
  })
  if (!allowed.ok) throw new Error(allowed.reason)

  const startingRole = loaded.inspection.jwtRole
  const user = loaded.user
  if (pendingSmokeProfile())
    throw new Error('Restore the pending smoke profile before starting another check.')
  const startingProfile = await getDocFromServer(doc(db, 'users', user.uid))
  if (!startingProfile.exists()) throw new Error('Starting demo profile is missing.')
  const startingAllSchools = startingProfile.data().allSchools ?? null
  const runId = `smoke-complete-${new Date().toISOString().replace(/[:.]/g, '-')}`
  const schoolId = resolveActiveSchoolId() || (await getCurrentSchoolId().catch(() => null))

  let userGovernance = normalizeUserGovernance({})
  try {
    const settingsSnap = await getDoc(doc(db, 'app_settings', 'global'))
    if (settingsSnap.exists()) {
      userGovernance = normalizeUserGovernance(settingsSnap.data()?.userGovernance)
    }
  } catch {
    // Defaults are fine for probes that do not need governance.
  }

  const ids = scenarioIds.filter((id) => scenarios.some((s) => s.id === id))
  const writesSelected = ids.some((id) => scenarios.find((s) => s.id === id)?.destructive)
  const rolesToRun = resolveCompleteCheckRoles(roles)
  if (rolesToRun.length === 0) {
    throw new Error('Select at least one rotator role for the complete check.')
  }

  const report = {
    runId,
    mode: 'complete',
    includeWrites: !!(includeWrites || writesSelected),
    keepArtifacts,
    caveat: COMPLETE_CHECK_CAVEAT,
    startedAt: new Date().toISOString(),
    finishedAt: null,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    user: {
      uid: user.uid,
      email: user.email,
      startingRole,
      restoredRole: null
    },
    schoolId,
    results: [],
    roles: [],
    restoreError: null,
    summary: { pass: 0, fail: 0, skip: 0, blocked: 0, rolesPass: 0, rolesFail: 0, rolesBlocked: 0 }
  }

  const progress = (results, extra) => {
    emitProgress(onProgress, results, extra)
    onReport?.(report)
  }
  onReport?.(report)

  const ctx = {
    user,
    runId,
    schoolId,
    roleClaim: startingRole,
    recoveryProfile: {
      uid: user.uid,
      role: startingRole,
      allSchools: startingAllSchools,
      schoolIds: loaded.tokenSchoolIds
    },
    tokenSchoolIds: loaded.tokenSchoolIds,
    userGovernance
  }

  devLog(`🤖 === COMPLETE CHECK ${runId} (${rolesToRun.length} roles) ===`)

  try {
    for (let i = 0; i < rolesToRun.length; i++) {
      const role = rolesToRun[i]
      const roleEntry = {
        role,
        label: roleLabel(role),
        status: 'pass',
        rotateError: null,
        cleanup: null,
        summary: { pass: 0, fail: 0, skip: 0, blocked: 0 },
        scenarios: {}
      }
      report.roles.push(roleEntry)

      progress(report.results, {
        currentRole: role,
        roleIndex: i + 1,
        roleCount: rolesToRun.length,
        phase: 'rotate'
      })

      try {
        await rotateDemoRoleInSession(role, { allSchools: false })
      } catch (err) {
        roleEntry.rotateError = err?.message || String(err)
        roleEntry.status = 'fail'
        report.results.push({
          role,
          scenario: 'rotate',
          step: `rotate to ${roleLabel(role)}`,
          status: 'fail',
          error: roleEntry.rotateError,
          durationMs: 0
        })
        progress(report.results, {
          currentRole: role,
          roleIndex: i + 1,
          roleCount: rolesToRun.length,
          phase: 'rotate'
        })
        continue
      }

      const after = await loadSmokeAuthInspection(authStore.currentUser)
      if (!after.signedIn || after.inspection.blocked) {
        const reason =
          after.inspection?.reasons?.join(' ') || 'Auth inspection failed after rotate.'
        roleEntry.rotateError = reason
        roleEntry.status = 'fail'
        report.results.push({
          role,
          scenario: 'rotate',
          step: `JWT ready for ${roleLabel(role)}`,
          status: 'fail',
          error: reason,
          durationMs: 0
        })
        continue
      }

      ctx.roleClaim = after.inspection.jwtRole
      ctx.tokenSchoolIds = after.tokenSchoolIds
      ctx.authInvalid = false

      progress(report.results, {
        currentRole: role,
        roleIndex: i + 1,
        roleCount: rolesToRun.length,
        phase: 'run'
      })

      await executeSmokeScenarios({
        scenarioIds: ids,
        ctx,
        results: report.results,
        onProgress: (results) =>
          progress(results, {
            currentRole: role,
            roleIndex: i + 1,
            roleCount: rolesToRun.length,
            phase: 'run'
          }),
        role,
        scenarioStatusOut: roleEntry.scenarios
      })

      const roleSteps = report.results.filter((r) => r.role === role)
      roleEntry.summary = summarizeSmokeResults(roleSteps)
      roleEntry.status = scenarioStatusFromSteps(roleSteps)

      if (ctx.authInvalid) break

      if (writesSelected && !keepArtifacts) {
        progress(report.results, {
          currentRole: role,
          roleIndex: i + 1,
          roleCount: rolesToRun.length,
          phase: 'cleanup'
        })
        try {
          roleEntry.cleanup = await cleanupSmokeArtifacts()
        } catch (err) {
          roleEntry.cleanup = {
            deleted: 0,
            storageDeleted: 0,
            errors: [err?.message || String(err)]
          }
        }
      }
    }

    if (writesSelected && !keepArtifacts) {
      try {
        report.cleanup = await cleanupSmokeArtifacts()
      } catch (err) {
        report.cleanup = { deleted: 0, storageDeleted: 0, errors: [err?.message || String(err)] }
        devError('Complete check final cleanup failed:', err)
      }
    }
  } catch (err) {
    report.runError = err?.message || String(err)
    throw err
  } finally {
    progress(report.results, {
      currentRole: startingRole,
      roleIndex: rolesToRun.length,
      roleCount: rolesToRun.length,
      phase: 'restore'
    })
    try {
      if (pendingSmokeProfile()) await restoreSmokeProfile()
      await rotateDemoRoleInSession(startingRole, { allSchools: startingAllSchools })
      report.user.restoredRole = startingRole
    } catch (err) {
      report.restoreError = err?.message || String(err)
      devError('Complete check could not restore starting role:', err)
    }
    onReport?.(report)
  }

  report.finishedAt = new Date().toISOString()
  const totals = summarizeSmokeResults(report.results)
  report.summary = {
    ...totals,
    rolesPass: report.roles.filter((r) => r.status === 'pass').length,
    rolesFail: report.roles.filter((r) => r.status === 'fail').length,
    rolesBlocked: report.roles.filter((r) => r.status === 'blocked').length
  }

  devLog(
    `🤖 === COMPLETE CHECK DONE: ${report.summary.rolesPass} roles pass, ${report.summary.rolesFail} fail, ${report.summary.rolesBlocked} blocked ===`,
    report
  )
  onReport?.(report)
  return report
}
